import * as Sentry from '@sentry/react'
import { currentAlertFeature } from './feature-from-route'
import { resolveRouteUuids } from './paywall-route-uuids'

// Studio has no env-name variable; the API host is what differs between the
// deploys (STUDIO_CONFIGS in infrastructure/configs/generic.js).
export const resolveSentryEnvironment = ({ nodeEnv, override, apiHost } = {}) => {
  if (override) return override
  if (nodeEnv !== 'production') return 'local'
  if (/^https:\/\/api\.nolanai\.app(\/|$)/.test(apiHost || '')) return 'production'
  return 'development'
}

export const SENTRY_ENVIRONMENT = resolveSentryEnvironment({
  nodeEnv: process.env.NODE_ENV,
  override: process.env.REACT_APP_SENTRY_ENVIRONMENT,
  apiHost: process.env.REACT_APP_REST_API_HOST
})

export const isProductionEnvironment = () => SENTRY_ENVIRONMENT === 'production'

// Trace target for the API's origin (api./devapi.nolanai.app), so Studio errors share a trace with
// the API's. Anchored: a string target matches any URL that merely contains it, third parties included.
export const apiTraceTarget = (apiHost) => {
  try {
    const escapedOrigin = new URL(apiHost).origin.replace(/[.*+?^$()|[\]\\]/g, '\\$&')
    return new RegExp(`^${escapedOrigin}(/|$)`)
  } catch (_) {
    return null
  }
}

export const identifySentryUser = (user) => {
  try {
    const id = user?.userUuid || user?.uid
    Sentry.setUser(id ? { id, email: user.email } : null)
    // Slack alerts can show tags but not user.email, so mirror it as a tag.
    Sentry.setTag('user_email', id ? user.email : undefined)
  } catch (_) {
    // Identity is best-effort; never let it break auth.
  }
}

// Table Read and Script Doctor are drawers on the editor route, so the route
// alone cannot name them; an open drawer overrides it.
let surfaceFeature = null

export const currentSentryFeature = () => surfaceFeature || currentAlertFeature()

export const applySentryFeatureTag = () => {
  try {
    // undefined drops the tag on routes with no product feature.
    Sentry.setTag('feature', currentSentryFeature() || undefined)
  } catch (_) {}
}

export const setSentrySurfaceFeature = (feature) => {
  surfaceFeature = feature || null
  applySentryFeatureTag()
}

// Ids are cleared on routes without them, so an event never names the last page's script.
export const applySentryRouteTags = (pathname) => {
  applySentryFeatureTag()
  try {
    const { projectUuid, scriptUuid } = resolveRouteUuids(pathname)
    Sentry.setTags({ project_uuid: projectUuid || undefined, script_uuid: scriptUuid || undefined })
  } catch (_) {}
}

const lowered = (value) => typeof value === 'string' && value.trim() ? value.trim().toLowerCase() : undefined

// Unknown values stay undefined, which drops the tag rather than sending "undefined".
export const resolveTriageTags = (user, workspace) => {
  if (!user) return { plan: undefined, plan_status: undefined, customer_type: undefined, entitlement: undefined, workspace_uuid: undefined }
  const plan = lowered(user.subscriptionPlan)
  // Raw Stripe status: user.subscriptionStatus defaults to 'Active' even with no subscription.
  const planStatus = lowered(user.subscriptionData?.data?.status)
  // A team workspace grants pro (getEffectiveSubscriptionPlan); only team ones reach the API.
  const teamWorkspace = workspace?.type === 'team' ? workspace : null
  const viaWorkspace = Boolean(teamWorkspace) && plan !== 'pro'
  const freePlan = !plan || plan === 'free'
  let customerType = 'paid'
  if (!viaWorkspace && freePlan) customerType = 'free'
  else if (!viaWorkspace && planStatus === 'trialing') customerType = 'trial'
  // Same values as the API's entitlement tag; giveaway is Studio-only detail.
  let entitlement
  if (viaWorkspace) entitlement = 'workspace'
  else if (user.subscriptionData?.isGiveaway) entitlement = 'giveaway'
  else if (!freePlan) entitlement = 'subscription'
  return {
    plan,
    plan_status: planStatus,
    customer_type: customerType,
    entitlement,
    workspace_uuid: teamWorkspace?.uuid || teamWorkspace?.workspace_uuid || undefined
  }
}

export const setSentryTriageContext = (user, workspace) => {
  try {
    Sentry.setTags(resolveTriageTags(user, workspace))
  } catch (_) {}
}
